import { create } from "zustand";
import { immer } from "zustand/middleware/immer";
import { persist, createJSONStorage } from "zustand/middleware";
import type { PreviewTarget } from "@/components/files-browser/preview-target";
import {
  DEFAULT_WALLPAPER,
  type WallpaperKey,
} from "@/components/computer/desktop/wallpapers";

export type ComputerTab =
  "files" | "browser" | "terminal" | "pieces" | "app" | "preview";

/** The surface the AGENT is currently using in a task. 'browser' | 'app' drive
 *  the split strip's slot-1 promotion; 'terminal' | 'files' are desktop-only
 *  presence stamps (set by the tool handler only when the desktop flag is on,
 *  so the split world never sees them). */
export type AgentSurface = "browser" | "app" | "terminal" | "files";

/** Sentinel taskId for the Knowledge page's FilesBrowser (no real task).
 *  Canonical home is here (not FilesBrowser.tsx) because `partialize`
 *  below must exclude this slot without importing a component. */
export const KNOWLEDGE_BROWSER_KEY = "__knowledge-browser__";

/** Top-level My files sections shown as folders in the tree.
 *  'this-task' is the only one wired to real data today; 'workspace' will
 *  show the user's full files/sandbox file tree (volume template); the
 *  others are placeholders pending the files-API integration follow-up.
 *  'creations' + 'temp' are the Creations P1 re-root folders.
 *  'media' is the Media system folder (Finder-root redesign) — the canvas
 *  view over the content-engine artifact store. */
export type FolderId =
  | "this-task"
  | "workspace"
  | "uploaded"
  | "shared"
  | "creations"
  | "temp"
  | "media";

/** A per-task selection is structurally mutually exclusive: either a file
 *  is selected, or a folder, or nothing. Replaces the parallel
 *  `selectedFileId` / `selectedFolderId` flat fields, where actions had to
 *  remember to null one when setting the other (a recurring source of
 *  bugs — see the stale-selection regression in FileQuickLookCard). */
export type Selection =
  | { kind: "none" }
  | { kind: "file"; fileId: string }
  | { kind: "folder"; folderId: FolderId };

interface PerTaskState {
  isOpen: boolean;
  activeTab: ComputerTab;
  selection: Selection;
  /** True if the user has manually closed Computer in this task at least once. */
  hasUserClosed: boolean;
  /** Files-tab search query — filters folder views by filename. */
  filesSearch: string;
  /** Files-tab sort field. */
  filesSortBy: FilesSortBy;
  /** Files-tab sort direction. */
  filesSortDir: SortDir;
  /** Grid (icon tiles) vs list (filename + date row) view. */
  filesViewMode: FilesViewMode;
  /** Drilled-in user folder UUID inside the workspace ('My files') view.
   *  `null` means we're at the workspace root. Separate from `selection`
   *  because the top-level navigation (workspace / uploaded / shared /
   *  this-task) lives in `selection.folderId`, while this is the in-canvas
   *  breadcrumb position within the workspace branch. */
  workspaceFolderId: string | null;
  /** SESSION-ONLY (stripped in partialize): what the agent is doing right
   *  now. A persisted value would claim activity that is not happening. */
  agentContext: AgentSurface | null;
  /** SESSION-ONLY (stripped in partialize): the file behind the Preview tab.
   *  PreviewTargets carry signed URLs that expire — persisting one would
   *  restore a dead pane (same failure class as stored authUrls). */
  previewTarget: PreviewTarget | null;
  /** SESSION-ONLY: when agentContext was last stamped (ms epoch). The desktop
   *  treats a surface as live for PRESENCE_TTL_MS after this, then stale. */
  agentSurfaceAt: number | null;
  /** SESSION-ONLY: desktop world — a window is open over the wallpaper. */
  desktopWindowOpen: boolean;
  /** Persisted: when the user last opened Previews (ms epoch). Creations
   *  created after this count as new on the dock badge. */
  creationsSeenAt: number;
  /** SESSION-ONLY: the app the Apps window shows, by its port; null = this
   *  task's own app. Set by the app the person opened or picked in the list. */
  appPort: number | null;
}

export type FilesSortBy = "name" | "date" | "size" | "type";
export type SortDir = "asc" | "desc";
export type FilesViewMode = "grid" | "list";

const DEFAULT_PER_TASK: PerTaskState = {
  isOpen: false,
  activeTab: "files",
  selection: { kind: "none" },
  hasUserClosed: false,
  filesSearch: "",
  filesSortBy: "date",
  filesSortDir: "desc",
  filesViewMode: "grid",
  workspaceFolderId: null,
  agentContext: null,
  previewTarget: null,
  agentSurfaceAt: null,
  desktopWindowOpen: false,
  creationsSeenAt: 0,
  appPort: null,
};

interface ComputerState {
  byTask: Record<string, PerTaskState>;
  /** Desktop world settings — persisted, user-level (not per task).
   *  `chatWidth`: the chat column's width in px beside the computer, set by
   *  dragging the seam; null = the default split. */
  desktop: { wallpaper: WallpaperKey; chatWidth: number | null };

  // selectors
  isOpen: (taskId: string) => boolean;
  activeTab: (taskId: string) => ComputerTab;
  selection: (taskId: string) => Selection;
  /** @deprecated Use `selection(taskId)` and discriminate on `kind`. */
  selectedFileId: (taskId: string) => string | null;
  /** @deprecated Use `selection(taskId)` and discriminate on `kind`. */
  selectedFolderId: (taskId: string) => FolderId | null;
  filesSearch: (taskId: string) => string;
  filesSortBy: (taskId: string) => FilesSortBy;
  filesSortDir: (taskId: string) => SortDir;
  filesViewMode: (taskId: string) => FilesViewMode;
  workspaceFolderId: (taskId: string) => string | null;
  agentContext: (taskId: string) => AgentSurface | null;
  previewTargetOf: (taskId: string) => PreviewTarget | null;
  agentSurfaceAt: (taskId: string) => number | null;
  desktopWindowOpen: (taskId: string) => boolean;
  creationsSeenAt: (taskId: string) => number;
  appPort: (taskId: string) => number | null;
  /** Split V1 tab strip order: the surface the task is using sits first.
   *  User attention (an open preview) outranks the agent's live surface. */
  stripOrder: (taskId: string) => ComputerTab[];

  // actions
  open: (taskId: string) => void;
  close: (taskId: string) => void;
  onFileGenerated: (taskId: string, fileId: string) => void;
  onAgentBrowserActivity: (taskId: string) => void;
  selectFile: (taskId: string, fileId: string) => void;
  selectFolder: (taskId: string, folderId: FolderId) => void;
  clearSelection: (taskId: string) => void;
  setActiveTab: (taskId: string, tab: ComputerTab) => void;
  openWithFile: (taskId: string, fileId: string) => void;
  /** Open the panel on the Apps window — the story card's Open. With a port,
   *  on that app: a task can have several, and Open names the one clicked. */
  openWithApp: (taskId: string, port?: number) => void;
  /** The Apps window's list: the person picked this app. */
  selectApp: (taskId: string, port: number) => void;
  /** Split V1: a dev server announced a live frame — promote Apps to slot 1. */
  onAppLive: (taskId: string) => void;
  /** An announce arrived (task:preview_ready): open the panel on Apps so the
   *  payoff is in front of the user, not behind the Computer button. */
  onAppAnnounced: (taskId: string) => void;
  /** Split V1: user clicked a creation or file — the Preview tab claims slot 1. */
  openPreviewTarget: (taskId: string, target: PreviewTarget) => void;
  closePreview: (taskId: string) => void;
  /** Desktop presence from tool fires (Bash → terminal, file tools → files).
   *  Stamps only; never opens the panel or moves the active tab. */
  onAgentToolSurface: (
    taskId: string,
    surface: "terminal" | "files",
    now?: number
  ) => void;
  /** AGENT_END: the agent stopped, so no surface is live. */
  clearAgentSurface: (taskId: string) => void;
  /** Desktop: a dock click. */
  openWindow: (taskId: string, tab: ComputerTab) => void;
  /** The agent's SHOW verb — opens the panel + window and overrides a sticky
   *  manual close (the user asked for this through the agent). */
  showSurface: (taskId: string, tab: ComputerTab) => void;
  /** Desktop: the window's close button / Escape / wallpaper click. */
  closeWindow: (taskId: string) => void;
  /** Desktop: the Previews dock item — the open preview if there is one,
   *  else Files on the Creations folder. Marks creations seen only on that
   *  second branch: one open file is not the folder. */
  openPreviews: (taskId: string, now?: number) => void;
  markCreationsSeen: (taskId: string, now?: number) => void;
  setWallpaper: (key: WallpaperKey) => void;
  /** Desktop: the seam between chat and computer settled here (null = default). */
  setChatWidth: (width: number | null) => void;
  setFilesSearch: (taskId: string, query: string) => void;
  setFilesSort: (taskId: string, sortBy: FilesSortBy, sortDir: SortDir) => void;
  setFilesViewMode: (taskId: string, mode: FilesViewMode) => void;
  setWorkspaceFolderId: (taskId: string, folderId: string | null) => void;
}

/** Pure strip-order derivation, exported so components can derive the order
 *  from primitive subscriptions (agentContext + previewOpen) instead of a
 *  store selector that mints a fresh array on every store change (which
 *  would defeat zustand's Object.is bail-out and re-render at stream rate). */
export function computeStripOrder(
  agentContext: AgentSurface | null,
  previewOpen: boolean
): ComputerTab[] {
  const GENERAL: ComputerTab[] = ["files", "browser", "app", "terminal"];
  if (previewOpen) return ["preview", ...GENERAL];
  if (!agentContext) return GENERAL;
  return [agentContext, ...GENERAL.filter((x) => x !== agentContext)];
}

/** Ensure a per-task slot exists on the draft and return it. Replaces the
 *  five-line `if (!draft.byTask[taskId]) draft.byTask[taskId] = {...}` guard
 *  that was repeated in every action (flagged by two code reviews). */
type Draft = {
  byTask: Record<string, PerTaskState>;
  desktop: { wallpaper: WallpaperKey; chatWidth: number | null };
};
function ensureTask(draft: Draft, taskId: string): PerTaskState {
  if (!draft.byTask[taskId]) draft.byTask[taskId] = { ...DEFAULT_PER_TASK };
  return draft.byTask[taskId];
}

/** v1 persisted shape — kept here so the migrator can read it without
 *  exporting the old types from production code. */
interface PerTaskStateV1 {
  isOpen?: boolean;
  activeTab?: ComputerTab;
  selectedFileId?: string | null;
  selectedFolderId?: FolderId | null;
  hasUserClosed?: boolean;
}

// localStorage-backed so the Computer's open/active-tab/selection state
// survives a refresh and a task reopen. Without this the panel always
// reverted to closed-with-nothing-selected, which was disorienting when
// a user came back to a task they'd been actively working in.
//
// `partialize` strips out functions (selectors / actions) so only the
// per-task slot map is serialised. The map is a plain object — Map would
// need a custom replacer/reviver.
//
// Storage key is `vestra-computer-state-v1` (the legacy name); the v2
// upgrade rides on the zustand-persist `version` field, not the key.
export const useComputerStore = create<ComputerState>()(
  persist(
    immer((set, get) => ({
      byTask: {},
      desktop: { wallpaper: DEFAULT_WALLPAPER, chatWidth: null },
      isOpen: (taskId) =>
        get().byTask[taskId]?.isOpen ?? DEFAULT_PER_TASK.isOpen,
      activeTab: (taskId) =>
        get().byTask[taskId]?.activeTab ?? DEFAULT_PER_TASK.activeTab,
      selection: (taskId) =>
        get().byTask[taskId]?.selection ?? DEFAULT_PER_TASK.selection,
      selectedFileId: (taskId) => {
        const s = get().byTask[taskId]?.selection ?? DEFAULT_PER_TASK.selection;
        return s.kind === "file" ? s.fileId : null;
      },
      selectedFolderId: (taskId) => {
        const s = get().byTask[taskId]?.selection ?? DEFAULT_PER_TASK.selection;
        return s.kind === "folder" ? s.folderId : null;
      },
      filesSearch: (taskId) =>
        get().byTask[taskId]?.filesSearch ?? DEFAULT_PER_TASK.filesSearch,
      filesSortBy: (taskId) =>
        get().byTask[taskId]?.filesSortBy ?? DEFAULT_PER_TASK.filesSortBy,
      filesSortDir: (taskId) =>
        get().byTask[taskId]?.filesSortDir ?? DEFAULT_PER_TASK.filesSortDir,
      filesViewMode: (taskId) =>
        get().byTask[taskId]?.filesViewMode ?? DEFAULT_PER_TASK.filesViewMode,
      workspaceFolderId: (taskId) =>
        get().byTask[taskId]?.workspaceFolderId ??
        DEFAULT_PER_TASK.workspaceFolderId,
      agentContext: (taskId) => get().byTask[taskId]?.agentContext ?? null,
      previewTargetOf: (taskId) => get().byTask[taskId]?.previewTarget ?? null,
      agentSurfaceAt: (taskId) => get().byTask[taskId]?.agentSurfaceAt ?? null,
      desktopWindowOpen: (taskId) =>
        get().byTask[taskId]?.desktopWindowOpen ?? false,
      creationsSeenAt: (taskId) => get().byTask[taskId]?.creationsSeenAt ?? 0,
      appPort: (taskId) => get().byTask[taskId]?.appPort ?? null,
      stripOrder: (taskId) => {
        const t = get().byTask[taskId];
        return computeStripOrder(t?.agentContext ?? null, !!t?.previewTarget);
      },

      open: (taskId: string) =>
        set((draft) => {
          ensureTask(draft, taskId).isOpen = true;
        }),
      close: (taskId: string) =>
        set((draft) => {
          const t = ensureTask(draft, taskId);
          t.isOpen = false;
          t.hasUserClosed = true;
        }),
      onFileGenerated: (taskId: string, fileId: string) =>
        set((draft) => {
          const t = ensureTask(draft, taskId);
          // Auto-open only if user hasn't closed it this task.
          if (!t.hasUserClosed) t.isOpen = true;
          // First file wins for selection; only set if nothing is selected yet
          // (covers both empty and folder-view states).
          if (t.selection.kind === "none") {
            t.selection = { kind: "file", fileId };
          }
        }),
      /** Auto-open the Computer panel on the Browser tab when the agent
       *  starts driving the headless browser. Mirrors onFileGenerated's
       *  gate: respects a manual close so a user who deliberately closed
       *  the panel doesn't get it shoved open again on every action. */
      onAgentBrowserActivity: (taskId: string) =>
        set((draft) => {
          const t = ensureTask(draft, taskId);
          if (!t.hasUserClosed) t.isOpen = true;
          // Split V1: the Browser tab is now the surface in use — slot 1.
          t.agentContext = "browser";
          // Snap to the Browser tab so the user actually sees what the
          // agent is doing — files might still be the active tab from a
          // previous interaction. Honor the user's later tab choice; we
          // only switch when the agent kicks off browser activity.
          t.activeTab = "browser";
          t.agentSurfaceAt = Date.now();
          t.desktopWindowOpen = true;
        }),
      selectFile: (taskId: string, fileId: string) =>
        set((draft) => {
          ensureTask(draft, taskId).selection = { kind: "file", fileId };
        }),
      selectFolder: (taskId: string, folderId: FolderId) =>
        set((draft) => {
          ensureTask(draft, taskId).selection = { kind: "folder", folderId };
        }),
      clearSelection: (taskId: string) =>
        set((draft) => {
          ensureTask(draft, taskId).selection = { kind: "none" };
        }),
      setActiveTab: (taskId: string, tab: ComputerTab) =>
        set((draft) => {
          ensureTask(draft, taskId).activeTab = tab;
        }),
      openWithFile: (taskId: string, fileId: string) =>
        set((draft) => {
          const t = ensureTask(draft, taskId);
          t.isOpen = true;
          t.activeTab = "files";
          t.selection = { kind: "file", fileId };
          t.hasUserClosed = false; // explicit click resets the sticky-close
          t.desktopWindowOpen = true;
        }),
      openWithApp: (taskId: string, port?: number) =>
        set((draft) => {
          const t = ensureTask(draft, taskId);
          t.isOpen = true;
          t.activeTab = "app";
          t.agentContext = "app"; // the app is the surface in use — slot 1
          t.hasUserClosed = false; // explicit click resets the sticky-close
          t.agentSurfaceAt = Date.now();
          t.desktopWindowOpen = true;
          if (port !== undefined) t.appPort = port;
        }),
      selectApp: (taskId: string, port: number) =>
        set((draft) => {
          ensureTask(draft, taskId).appPort = port;
        }),
      /** A dev server announced (or re-announced) a live frame for this task.
       *  Promotes Apps to slot 1 and snaps the view there. Mirrors
       *  onAgentBrowserActivity's contract minus the isOpen flip: the panel's
       *  auto-reveal effect only runs while the panel is mounted (open), and
       *  the story card path opens via openWithApp. */
      onAppLive: (taskId: string) =>
        set((draft) => {
          const t = ensureTask(draft, taskId);
          t.agentContext = "app";
          t.activeTab = "app";
          t.agentSurfaceAt = Date.now();
          t.desktopWindowOpen = true;
        }),
      /** The announce IS the payoff ("build it and show me"), and it must not
       *  land behind a closed panel: the in-panel auto-reveal only runs while
       *  the panel is mounted, so with the panel closed the user had to find
       *  the Computer button themselves (founder, 08-28: "I had to click
       *  computer and open this"). Mirrors onAgentBrowserActivity's
       *  manual-close gate: a user who deliberately closed the panel this task
       *  is not shoved back in. */
      onAppAnnounced: (taskId: string) =>
        set((draft) => {
          const t = ensureTask(draft, taskId);
          if (!t.hasUserClosed) t.isOpen = true;
          t.agentContext = "app";
          t.activeTab = "app";
          t.agentSurfaceAt = Date.now();
          t.desktopWindowOpen = true;
        }),
      openPreviewTarget: (taskId: string, target: PreviewTarget) =>
        set((draft) => {
          const t = ensureTask(draft, taskId);
          t.previewTarget = target;
          t.isOpen = true;
          t.activeTab = "preview";
          t.hasUserClosed = false; // explicit click resets the sticky-close
          t.desktopWindowOpen = true;
        }),
      closePreview: (taskId: string) =>
        set((draft) => {
          const t = ensureTask(draft, taskId);
          t.previewTarget = null;
          // Fall back to the agent's live surface, else Files.
          if (t.activeTab === "preview")
            t.activeTab = t.agentContext ?? "files";
        }),
      onAgentToolSurface: (
        taskId: string,
        surface: "terminal" | "files",
        now = Date.now()
      ) =>
        set((draft) => {
          const t = ensureTask(draft, taskId);
          t.agentContext = surface;
          t.agentSurfaceAt = now;
        }),
      clearAgentSurface: (taskId: string) =>
        set((draft) => {
          const t = ensureTask(draft, taskId);
          t.agentContext = null;
          t.agentSurfaceAt = null;
        }),
      openWindow: (taskId: string, tab: ComputerTab) =>
        set((draft) => {
          const t = ensureTask(draft, taskId);
          t.activeTab = tab;
          t.desktopWindowOpen = true;
        }),
      /** The agent's SHOW verb (computer:show): the user's own ask carried
       *  through the agent, so it opens the panel AND overrides a sticky manual
       *  close — unlike the ambient auto-open paths, which respect it. */
      showSurface: (taskId: string, tab: ComputerTab) =>
        set((draft) => {
          const t = ensureTask(draft, taskId);
          t.isOpen = true;
          t.hasUserClosed = false;
          t.activeTab = tab;
          t.desktopWindowOpen = true;
        }),
      /** The close is honored until the agent's next surface activity, which
       *  reopens the window on purpose: the desktop shows what the agent is
       *  doing, and the dock halo alone is not enough to convey it. Mirrors
       *  onAgentBrowserActivity's tab snap in the split world — deliberately
       *  NOT gated on hasUserClosed. */
      closeWindow: (taskId: string) =>
        set((draft) => {
          const t = ensureTask(draft, taskId);
          t.desktopWindowOpen = false;
          if (t.activeTab === "preview") {
            t.previewTarget = null;
            t.activeTab =
              t.agentContext === "browser" || t.agentContext === "app"
                ? t.agentContext
                : "files";
          }
        }),
      openPreviews: (taskId: string, now = Date.now()) =>
        set((draft) => {
          const t = ensureTask(draft, taskId);
          if (t.previewTarget) {
            // One open file — the user never saw the Creations folder, so the
            // badge must keep counting the creations they haven't looked at.
            t.activeTab = "preview";
          } else {
            t.activeTab = "files";
            t.selection = { kind: "folder", folderId: "creations" };
            t.creationsSeenAt = now;
          }
          t.desktopWindowOpen = true;
        }),
      markCreationsSeen: (taskId: string, now = Date.now()) =>
        set((draft) => {
          ensureTask(draft, taskId).creationsSeenAt = now;
        }),
      setWallpaper: (key: WallpaperKey) =>
        set((draft) => {
          draft.desktop.wallpaper = key;
        }),
      setChatWidth: (width: number | null) =>
        set((draft) => {
          draft.desktop.chatWidth = width;
        }),
      setFilesSearch: (taskId: string, query: string) =>
        set((draft) => {
          ensureTask(draft, taskId).filesSearch = query;
        }),
      setFilesSort: (taskId: string, sortBy: FilesSortBy, sortDir: SortDir) =>
        set((draft) => {
          const t = ensureTask(draft, taskId);
          t.filesSortBy = sortBy;
          t.filesSortDir = sortDir;
        }),
      setFilesViewMode: (taskId: string, mode: FilesViewMode) =>
        set((draft) => {
          ensureTask(draft, taskId).filesViewMode = mode;
        }),
      setWorkspaceFolderId: (taskId: string, folderId: string | null) =>
        set((draft) => {
          ensureTask(draft, taskId).workspaceFolderId = folderId;
        }),
    })),
    {
      // Storage key bumped 2026-05-24 from `vestra-computer-state-v1` →
      // `-v2`. The in-memory schema migrated cleanly (Selection union),
      // but the persist `migrate()` callback was leaving stale v1 entries
      // in the old localStorage key, and downstream reads were getting
      // confused state. A key rename forces every browser to start fresh
      // on the v2 shape; the old key is abandoned harmlessly (users
      // re-open the Computer panel and re-select files as needed — both
      // are one-click actions, no real data lost).
      name: "vestra-computer-state-v2",
      storage: createJSONStorage(() => localStorage),
      // The Knowledge browser's slot is never persisted (founder 08-04):
      // opening Knowledge must always show the default root, not a folder
      // restored from a previous session. Per-task slots keep persisting.
      partialize: (state) => ({
        byTask: Object.fromEntries(
          (Object.entries(state.byTask) as [string, PerTaskState][])
            .filter(([key]) => key !== KNOWLEDGE_BROWSER_KEY)
            .map(([key, slot]) => [
              key,
              {
                ...slot,
                // SESSION-ONLY: a persisted PreviewTarget carries signed URLs
                // that expire, and a persisted agentContext claims activity
                // that is not happening. Both reset on reload.
                agentContext: null,
                previewTarget: null,
                agentSurfaceAt: null,
                desktopWindowOpen: false,
                appPort: null,
                // A restored activeTab of 'preview' would render a dead pane
                // (its target is gone) — land on Files instead.
                activeTab:
                  slot.activeTab === "preview"
                    ? ("files" as const)
                    : slot.activeTab,
              },
            ])
        ),
        desktop: state.desktop,
      }),
      version: 10,
      // v1 → v2: collapse the parallel selectedFileId / selectedFolderId
      // pair into a single discriminated `selection`. The pair was already
      // mutually exclusive at the action layer, so every v1 slot maps to
      // exactly one v2 variant (file > folder > none, in that priority).
      // v2 → v3: ComputerTab gained 'terminal'. Existing values
      // ('files' | 'browser') are forward-compatible — no slot remapping
      // needed. We bump the version anyway so future migrations can branch
      // on fromVersion < 3.
      migrate: (persistedState, fromVersion) => {
        if (fromVersion < 2) {
          const old = (persistedState ?? {}) as {
            byTask?: Record<string, PerTaskStateV1>;
          };
          const next: Record<string, PerTaskState> = {};
          for (const [taskId, slot] of Object.entries(old.byTask ?? {})) {
            const selection: Selection = slot.selectedFileId
              ? { kind: "file", fileId: slot.selectedFileId }
              : slot.selectedFolderId
                ? { kind: "folder", folderId: slot.selectedFolderId }
                : { kind: "none" };
            next[taskId] = {
              isOpen: slot.isOpen ?? DEFAULT_PER_TASK.isOpen,
              activeTab: slot.activeTab ?? DEFAULT_PER_TASK.activeTab,
              hasUserClosed:
                slot.hasUserClosed ?? DEFAULT_PER_TASK.hasUserClosed,
              selection,
              filesSearch: DEFAULT_PER_TASK.filesSearch,
              filesSortBy: DEFAULT_PER_TASK.filesSortBy,
              filesSortDir: DEFAULT_PER_TASK.filesSortDir,
              filesViewMode: DEFAULT_PER_TASK.filesViewMode,
              workspaceFolderId: DEFAULT_PER_TASK.workspaceFolderId,
              agentContext: null,
              previewTarget: null,
              creationsSeenAt: 0,
              agentSurfaceAt: null,
              desktopWindowOpen: false,
              appPort: null,
            };
          }
          persistedState = { byTask: next };
        }
        if (fromVersion < 3) {
          // v2 → v3: ComputerTab gained 'terminal'. Existing values
          // ('files' | 'browser') are forward-compatible — no slot
          // remapping needed. We bump the version anyway so future
          // migrations can branch on fromVersion < 3.
        }
        if (fromVersion < 4) {
          // v3 → v4: ComputerTab gained 'studio'. Existing values
          // ('files' | 'browser' | 'terminal') are forward-compatible — no
          // slot remapping needed. Every per-task slot is preserved as-is.
        }
        if (fromVersion < 5) {
          // v4 → v5: ComputerTab gained 'pieces'. Existing values
          // ('files' | 'browser' | 'terminal' | 'studio') are forward-compatible
          // — no slot remapping needed. Every per-task slot is preserved as-is.
        }
        if (fromVersion < 6) {
          // v5 → v6: FolderId gained 'creations' + 'temp' (Creations P1). Existing
          // selections ('this-task' | 'workspace' | 'uploaded' | 'shared') are
          // forward-compatible; every per-task slot is preserved as-is.
        }
        if (fromVersion < 7) {
          // v6 → v7: FolderId gained 'media' (Finder-root redesign). Existing
          // selections are forward-compatible; every per-task slot is
          // preserved as-is.
        }
        if (fromVersion < 8) {
          // v7 → v8: PerTaskState gained session-only agentContext /
          // previewTarget (never persisted, but hydrated v7 slots must still
          // satisfy the type) and ComputerTab gained 'preview'.
          const old = (persistedState ?? {}) as {
            byTask?: Record<string, Partial<PerTaskState>>;
          };
          for (const slot of Object.values(old.byTask ?? {})) {
            slot.agentContext = null;
            slot.previewTarget = null;
            if (slot.activeTab === "preview") slot.activeTab = "files";
          }
        }
        if (fromVersion < 9) {
          // v8 → v9: desktop world. Root `desktop.wallpaper` gains a default
          // and every slot gains creationsSeenAt (0 = everything counts as
          // new until the user opens Previews once). Session-only fields
          // (agentSurfaceAt, desktopWindowOpen) are hydrated to their
          // defaults so a v8 slot satisfies the v9 type.
          const prev = (persistedState ?? {}) as {
            byTask?: Record<string, Partial<PerTaskState>>;
            desktop?: { wallpaper?: string };
          };
          const byTask = Object.fromEntries(
            Object.entries(prev.byTask ?? {}).map(([k, slot]) => [
              k,
              {
                ...DEFAULT_PER_TASK,
                ...slot,
                agentContext: null,
                previewTarget: null,
                agentSurfaceAt: null,
                desktopWindowOpen: false,
                creationsSeenAt:
                  typeof slot?.creationsSeenAt === "number"
                    ? slot.creationsSeenAt
                    : 0,
              },
            ])
          );
          persistedState = {
            byTask,
            desktop: {
              wallpaper:
                (prev.desktop?.wallpaper as WallpaperKey | undefined) ??
                DEFAULT_WALLPAPER,
            },
          };
        }
        if (fromVersion < 10) {
          // v9 → v10: the seam between chat and computer is draggable; the
          // settled width persists at the root. Older states start on the
          // default split.
          const prev = (persistedState ?? {}) as {
            byTask?: unknown;
            desktop?: { wallpaper?: WallpaperKey; chatWidth?: number | null };
          };
          persistedState = {
            ...prev,
            desktop: {
              wallpaper: prev.desktop?.wallpaper ?? DEFAULT_WALLPAPER,
              chatWidth:
                typeof prev.desktop?.chatWidth === "number"
                  ? prev.desktop.chatWidth
                  : null,
            },
          };
        }
        return persistedState;
      },
    }
  )
);
